import { useEffect, useMemo } from "react";
import type { Block } from "@/api/types";
import { TextBlock } from "./TextBlock";
import { ImageBlock } from "./ImageBlock";
import { DialogueBlock } from "./DialogueBlock";
import { SceneBlock } from "./SceneBlock";
import { ThoughtBlock } from "./ThoughtBlock";
import "./blocks.css";

/** dialogue 블록 who 행(캐릭터 이름·아바타). */
export type Speaker = { name: string; avatarUrl?: string | undefined };

type Props = {
  blocks: readonly Block[];
  /** speaker 'main' 대사의 화자(플롯 캐릭터). 없으면 who 행 없음. */
  speaker?: Speaker | undefined;
  /** 알 수 없는 타입을 만났을 때 1회 보고(Sentry 연동 전까지 console). */
  onUnknown?: (type: string) => void;
  className?: string;
};

const defaultReport = (type: string) => console.warn("[blocks] unknown block type", type);

const KNOWN = new Set<string>(["text", "image", "dialogue", "thought", "scene"]);

/**
 * 플롯 소개(intro)·인트로/첫 장면(firstScene)·채팅 첫 장면 공용 렌더러.
 * 블록 순서 그대로, text·dialogue 본문은 마크다운(shared parseNarrative). 컨테이너 폭은 --content-max(600). 알 수 없는 타입은 건너뛰고 1회 보고.
 */
export function BlockRenderer({ blocks, speaker, onUnknown = defaultReport, className }: Props) {
  const firstUnknown = useMemo(() => blocks.map((b) => String((b as { type?: unknown }).type)).find((t) => !KNOWN.has(t)), [blocks]);
  useEffect(() => {
    if (firstUnknown !== undefined) onUnknown(firstUnknown);
    // 보고는 블록 배열당 1회. onUnknown 레퍼런스 변경으로 재보고하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstUnknown]);

  if (blocks.length === 0) return null;

  return (
    <div className={className ? `blocks ${className}` : "blocks"}>
      {blocks.map((block, i) => {
        switch (block.type) {
          case "text":
            return <TextBlock key={i} body={block.body} />;
          case "image":
            return <ImageBlock key={i} url={block.url} alt={block.alt} ratio={block.ratio} />;
          case "dialogue":
            return <DialogueBlock key={i} body={block.body} speaker={block.speaker} main={speaker} />;
          case "thought":
            return <ThoughtBlock key={i} body={block.body} speaker={block.speaker} />;
          case "scene":
            return <SceneBlock key={i} body={block.body} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
